"use client"

/**
 * ScrollArea — Radix-backed scroll container with always-visible-on-hover
 * scrollbars that respect platform conventions.
 *
 * Use when you need an **explicitly bounded** scrolling region (lists in
 * popovers, sidebar nav trees, long config panels) and the default
 * browser scrollbar is too coarse or visually noisy.
 *
 * Accessibility:
 *  - Radix manages keyboard scrolling parity with native overflow.
 *  - The viewport is `role="region"`-compatible — give the wrapper an
 *    `aria-label` when the content is a meaningful landmark.
 */

import * as React from "react"
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui"

import { cn } from "../../lib/utils"
import { ScrollRegion } from "./scroll-region"

function ScrollArea({
  className,
  children,
  viewportLabel = "Scrollable content",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  /** Accessible name for the scroll viewport (WCAG 1.4.10 / keyboard scroll). */
  viewportLabel?: string
}) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport asChild>
        <ScrollRegion
          data-slot="scroll-area-viewport"
          label={viewportLabel}
          className="size-full rounded-[inherit]"
        >
          {children}
        </ScrollRegion>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none select-none p-px transition-colors",
        orientation === "vertical" &&
          "h-full w-2.5 border-s border-s-transparent",
        orientation === "horizontal" &&
          "h-2.5 w-full flex-col border-t border-t-transparent",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="relative flex-1 rounded-full bg-border hover:bg-border/80"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
